import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, concat, throwError } from 'rxjs';
import { catchError, concatMap, finalize, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../shared/services/auth/auth.service';
import { NavigationService } from '../../shared/services/navigation/navigation.service';
import { SESSIONSTORAGE_TOKEN } from '../../shared/utils/constants';
import { LoaderService } from '../components/loader/loader.service';
import { ErrorModelData, HttpErrorModel } from '../interfaces/error-model-data.interface';
import { IS_PUBLIC_API } from '../utils/is-public-api- context';
import { isApiAudit } from '../utils/isApiAudit';
import { CustomHttpParams } from './custom-http-params.class';
import { GenerateTokenService } from './generate-token/generate-token.service';

@Injectable()
export class HttpService implements HttpInterceptor {
  private authService: AuthService;
  private url: string;
  private pendingLoaderRequests = 0;

  constructor(
    private loaderService: LoaderService,
    private injector: Injector,
    private navigationService: NavigationService,
    private readonly generateTokenService: GenerateTokenService,
  ) {
    setTimeout(() => (this.authService = this.injector.get(AuthService)));
  }

  private error = <ErrorModelData>{
    description: '',
    key: '',
    message: '',
    lastUrlName: '',
    error: null,
  };

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const options: any = {};
    let loaderUse: boolean = false;
    const token: string = sessionStorage.getItem(SESSIONSTORAGE_TOKEN);

    if (req.params instanceof CustomHttpParams && req.params.loaderUse) {
      loaderUse = req.params.loaderUse;
      this.showLoader();
    }

    if (req.context.get(IS_PUBLIC_API)) {
      return this.secureApiPublic(req, next).pipe(
        finalize(() => {
          if (loaderUse) {
            this.hideLoader();
          }
        }),
      );
    }

    if (
      !req.url.includes(
        `${environment.apiBase}/mortgage/auth/${environment.api_version.auth}/token`,
      ) &&
      !req.url.includes(
        `${environment.apiBase}/mortgage/auth/${environment.api_version.auth}/token/holder/secondary`,
      ) &&
      !req.url.includes(`${environment.apiBase}/mortgages-config-params`) &&
      req.url.startsWith(environment.apiBase)
    ) {
      if (
        req.url.includes(
          `${environment.apiBase}/mortgages-docs/${environment.api_version.mortgagesDocs}/documents/uploadDocs`,
        )
      ) {
        options.headers = req.headers.set('Authorization', `Bearer ${token}`);
      } else {
        options.headers = req.headers
          .set('Content-Type', 'application/json')
          .set('Authorization', `Bearer ${token}`);
      }
    }

    if (this.authService && req.url.startsWith(environment.apiBase)) {
      this.authService.reloadTimeoutLogout();
    }

    return next.handle(req.clone(options)).pipe(
      finalize(() => {
        if (loaderUse) {
          this.hideLoader();
        }
      }),
      catchError((error: any, caught: any) => {
        this.error =
          error && error.error && typeof error.error === 'object'
            ? error.error
            : undefined;

        if (this.error) {
          this.error.message = this.error.message
            ? this.error.message
            : error.error.internalStatus;
          this.url = req.url;
        }

        if (isApiAudit(req.url)) {
          return throwError(this.error);
        }

        if (!this.error && this.authService) {
          if ((error && error.status === 401) || error.status === 403) {
            this.authService.openTimeoutLogoutModal();
          } else {
            if (!this.urlException()) {
              this.authService.openTimeoutLogoutModal(
                true,
                '¡Ups! Se ha producido un error',
              );
            }
          }
          this.authService.logout();
          return;
        }

        if (req.url.includes('signIn')) {
          return throwError(this.error);
        }

        if (this.urlException()) {
          return throwError(this.error);
        }

        if (this.handleHTTPStatus(error)) {
          return throwError(this.error);
        }

        if (typeof this.error === 'object' && this.isApiErrorResponse(this.error)) {
          return throwError(this.error);
        } else if (req.url.includes('revoke')) {
          return;
        } else {
          if (this.authService) {
            this.catchErrorApiManager();
          }
          return;
        }
      }),
    );
  }

  private handleHTTPStatus(error) {
    switch (error.status) {
      case 401:
      case 403:
        this.authService.openTimeoutLogoutModal();
        this.authService.logout();
        return true;
      case 406:
      case 500:
      case 503:
      case 504:
        const errorParsed = error.error;
        if (this.urlException()) {
        } else {
          this.navigationService.redirectToError(errorParsed);
        }
        return true;
      default:
        return false;
    }
  }

  private showLoader(): void {
    this.loaderService.show();
  }

  private hideLoader(): void {
    this.loaderService.hide();
  }

  urlException(): boolean {
    const listUrl = [
      `${environment.apiBase}/mortgages/${environment.api_version.mortgages_simulator}/mortgage/register`,
      `${environment.apiBase}/mortgages-events/${environment.api_version.events}/events`,
      `${environment.apiBase}/mortgages-dashboard/${environment.api_version.dashboard}/state/mortgage`,
      `${environment.apiBase}/mortgages-dashboard/${environment.api_version.dashboard}/state/signature`,
      `${environment.apiBase}/mortgages-dashboard/${environment.api_version.dashboard}/state/document`,
      `${environment.apiBase}/mortgages-dashboard/${environment.api_version.dashboard}/state/appraisal`,
      `${environment.apiBase}/mortgages-dashboard/${environment.api_version.dashboard}/state/request`,
      `${environment.apiBase}/mortgages/${environment.api_version.mortgages_simulator}/mortgage/data`,
      `${environment.apiBase}/mortgages-docs/${environment.api_version.mortgagesDocs}/documents`,
      `${environment.apiBase}/mortgage/auth/${environment.api_version.auth}/token`,
      `${environment.apiBase}/mortgages-wall/${environment.api_version.wall}`,
      `${environment.apiBase}/mortgages/${environment.api_version.mortgages_simulator}/mortgage/agent`,
      `${environment.apiBase}/mortgages/${environment.api_version.mortgages_simulator}/mortgage/clientId`,
      `${environment.apiBase}/i18n/${environment.api_version.i18n}/translations`,
    ];
    let findUrl: boolean = false;
    listUrl.forEach((element) => {
      if (this.url.includes(element)) {
        findUrl = true;
      }
    });
    return findUrl;
  }

  isApiErrorResponse(error: HttpErrorModel | any): error is HttpErrorModel {
    return 'message' in error;
  }

  catchErrorApiManager() {
    this.authService.openTimeoutLogoutModal(true, '¡Ups! Se ha producido un error');
    this.authService.logout();
  }

  private secureApiPublic(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    let maxRetries = 5;
    return this.generateTokenService.getToken().pipe(
      map((token) => request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })),
      concatMap((authReq: any) => next.handle(authReq)),
      catchError((error, restart) => {
        if (error.status === 401 && maxRetries > 0) {
          maxRetries--;
          return concat(this.generateTokenService.generateToken(), restart);
        }
        return throwError({
          ...error.error,
          message: error?.error?.internalStatus,
        });
      }),
    );
  }
}
